import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Images, Scissors, Users } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import { useSuperadminAuth } from '../../context/SuperadminAuthContext';
import {
  DataListTable,
  ErrorState,
  KeyValueList,
  LoadingState,
  MetricCard,
  SectionCard,
  formatDateTime,
  formatNumber,
} from '../../components/superadmin/SuperAdminUI';

const ShopOverview = () => {
  const { shop, shopId } = useOutletContext();
  const { getAdmins, getShopUsageSummary } = useSuperadminAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [admins, setAdmins] = useState([]);
  const [usageSummary, setUsageSummary] = useState(null);

  const loadOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [adminsResponse, usageResponse] = await Promise.all([
        getAdmins({ shop_id: shopId, page: 1, pageSize: 100 }),
        getShopUsageSummary(shopId, { range: 'month' }),
      ]);

      if (!adminsResponse?.ok || !usageResponse?.ok) {
        throw new Error('Failed to load shop overview');
      }

      setAdmins(adminsResponse.data || []);
      setUsageSummary(usageResponse.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load shop overview');
    } finally {
      setLoading(false);
    }
  }, [getAdmins, getShopUsageSummary, shopId]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const summaryCards = useMemo(() => ([
    {
      label: 'Admins',
      value: formatNumber(admins.length),
      icon: <Users className="h-5 w-5" />,
    },
    {
      label: 'Barbers',
      value: formatNumber(usageSummary?.summary?.barbers?.total),
      icon: <Scissors className="h-5 w-5" />,
    },
    {
      label: 'Clients',
      value: formatNumber(usageSummary?.summary?.clients?.active_total),
      icon: <Users className="h-5 w-5" />,
    },
    {
      label: 'AI scans',
      value: formatNumber(usageSummary?.summary?.ai?.total_scans),
      icon: <Images className="h-5 w-5" />,
    },
  ]), [admins.length, usageSummary]);

  return (
    <div className="space-y-6">
      {error ? <ErrorState error={error} onRetry={loadOverview} /> : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map((card) => (
          <MetricCard key={card.label} {...card} />
        ))}
      </div>

      <SectionCard
        title="Shop overview"
        subtitle="Resolved from the live shop directory and tenant-scoped usage summary."
        icon={<Building2 className="h-5 w-5" style={{ color: '#7d6633' }} />}
      >
        {loading ? (
          <LoadingState lines={4} />
        ) : (
          <KeyValueList
            items={[
              { label: 'Shop ID', value: shop?.id || shopId },
              { label: 'Slug', value: shop?.slug || 'Unavailable' },
              { label: 'Public booking ID', value: shop?.public_booking_id || 'Unavailable' },
              { label: 'Status', value: shop?.subscription_status || 'Unavailable' },
              { label: 'Booking total', value: formatNumber(usageSummary?.summary?.bookings?.total) },
              { label: 'Manual credits granted', value: formatNumber(usageSummary?.summary?.credits?.manual_credits_granted_total) },
            ]}
          />
        )}
      </SectionCard>

      <SectionCard
        title="Admin owners"
        subtitle="Admin accounts linked to this shop."
        icon={<Users className="h-5 w-5" style={{ color: '#7d6633' }} />}
      >
        {loading ? (
          <LoadingState lines={5} />
        ) : (
          <DataListTable
            columns={[
              { key: 'name', label: 'Name' },
              { key: 'email', label: 'Email' },
              { key: 'role', label: 'Role', render: (row) => row.primaryShop?.role_in_shop || row.role },
              { key: 'created_at', label: 'Created', render: (row) => formatDateTime(row.created_at) },
            ]}
            rows={admins.map((admin) => ({
              ...admin,
              primaryShop: (admin.shops || []).find((entry) => String(entry.id) === String(shopId)) || admin.shops?.[0] || null,
            }))}
            emptyTitle="No admins linked"
            emptyDescription="No admin owner was returned for this shop."
          />
        )}
      </SectionCard>
    </div>
  );
};

export default ShopOverview;
